import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logAirlinesFilter } from '../../../../../common/log-ga-event'
import { updateAirlineFiltersReq } from '../../../../reducers/filters/filterInfo/filterInfo'
import { updateAirlineFiltersUI } from '../../../../reducers/filters/airlinesFilter/airlinesFilter'

export const useAirlinesFilters = ({ changeHandler, isDynamic = false, isRecommended = false }) => {
  const dispatch = useDispatch()

  const { airlines, activeTabId } = useSelector((state) => {
    return {
      airlines: state?.airlinesFilter || [],
      activeTabId: state?.activeTabId,
    }
  })

  const cheapestPriceObj = useSelector((state) => state?.cheapestPrice || [])
  const cheapestPriceBySliceId = cheapestPriceObj.find((i) => i.sliceId === activeTabId - 1)
  const delayedStateUpdate = useCallback(
    (shouldFireChange = true) => {
      return (...args) => {
        const [payload, meta, airlines] = args
        dispatch(
          updateAirlineFiltersReq({
            payload,
            meta,
            airlines,
            sbsParams: {
              activeTabId,
            },
          })
        )

        // should not fire the listings call when the user selected none
        if (shouldFireChange && changeHandler) {
          changeHandler()
        }
      }
    },
    [dispatch, activeTabId, changeHandler]
  )

  const airlineHandleChange = useCallback(
    (changed) => {
      const code = Object.keys(changed)[0]
      const payload = { value: code, checked: changed[code] }
      const enabledFlag = changed[code]
      dispatch(updateAirlineFiltersUI({ payload }))

      if (!isRecommended) logAirlinesFilter({ airlineCode: code, isDynamic, enabled: enabledFlag })

      delayedStateUpdate(true)(payload, {}, airlines)
    },
    [dispatch, isRecommended, isDynamic, delayedStateUpdate, airlines]
  )

  const handleOnlyClick = useCallback(
    ({ value }) => {
      const payload = { value, checked: true }

      if (!isRecommended) logAirlinesFilter({ airlineCode: value, isOnly: true, isDynamic })
      dispatch(updateAirlineFiltersUI({ payload, meta: { isOnlyClick: true } }))
      delayedStateUpdate(true)(payload, { isOnlyClick: true }, airlines)
    },
    [isRecommended, isDynamic, dispatch, delayedStateUpdate, airlines]
  )

  const allClick = useCallback(() => {
    dispatch(updateAirlineFiltersUI({ payload: {}, meta: { isAllClick: true } }))
    delayedStateUpdate(true)({}, { isAllClick: true }, airlines)
  }, [dispatch, delayedStateUpdate, airlines])

  const resetClick = useCallback(
    ({ shouldFireChange = true } = {}) => {
      dispatch(updateAirlineFiltersUI({ payload: {}, meta: { isNoneClick: true } }))
      delayedStateUpdate(shouldFireChange)({}, { isNoneClick: true }, airlines)
    },
    [dispatch, delayedStateUpdate, airlines]
  )

  const isSelectAllDisabled = airlines?.filter((i) => i.checked).length === airlines?.length
  const isSelectNoneDisabled = airlines?.filter((i) => i.checked).length === 0 || airlines?.length === 1

  return {
    airlines: useMemo(
      () =>
        airlines.map((a) => ({
          ...a,
          isCheapest:
            Number.parseInt(a.secondaryLabel?.replaceAll(/\D/g, ''), 10) ===
            Math.ceil(cheapestPriceBySliceId?.cheapestPrice),
        })),
      [airlines, cheapestPriceBySliceId]
    ),
    airlineHandleChange,
    resetClick,
    allClick,
    handleOnlyClick,
    isSelectAllDisabled,
    isSelectNoneDisabled,
  }
}
